import type { ExchangeId } from "../../types";
import {
  EXCHANGE_META,
  type FeeSummaryMeta,
  type LightningStatus,
  type WithdrawalLimitMeta,
  type WithdrawalLimitMetaItem,
} from "../../config/exchangeMeta";
import { createNumberFormatter } from "../lib/intl";
import { CompareIcon } from "./icons";

interface ExchangeComparisonRow {
  id: ExchangeId;
  name: string;
  logo?: string;
  logoWebp?: string;
  link?: string;
  mid?: number;
  withdrawalFeeBtc?: number;
  supportsLightning: boolean;
  lightningStatus: LightningStatus;
  lightningNote?: string;
  withdrawalLimits?: WithdrawalLimitMeta;
  effectiveToman?: number;
  feeSummary?: FeeSummaryMeta;
  takerFee?: number;
}

interface ExchangeComparisonTableProps {
  rows: ExchangeComparisonRow[];
  mode: "buy" | "sell";
  btcPriceToman?: number | null;
}

const tomanFormatter = createNumberFormatter("fa-IR", { maximumFractionDigits: 0 });
const percentFormatter = createNumberFormatter("fa-IR", { style: "percent", minimumFractionDigits: 2, maximumFractionDigits: 2 });
const satoshiFormatter = createNumberFormatter("fa-IR", { maximumFractionDigits: 0 });
const LAST_UPDATED_SHAMSI = "۵ مهر ۱۴۰۴";

export function ExchangeComparisonTable({ rows, mode, btcPriceToman }: ExchangeComparisonTableProps) {
  const bestRow = rows.reduce<ExchangeComparisonRow | null>((acc, row) => {
    const currentValue = typeof row.effectiveToman === "number" && Number.isFinite(row.effectiveToman)
      ? row.effectiveToman
      : null;
    if (currentValue === null) {
      return acc;
    }
    if (!acc || typeof acc.effectiveToman !== "number" || !Number.isFinite(acc.effectiveToman)) {
      return row;
    }
    if (mode === "buy") {
      return currentValue < acc.effectiveToman ? row : acc;
    }
    return currentValue > acc.effectiveToman ? row : acc;
  }, null);

  const renderWithdrawalLimitCell = (item?: WithdrawalLimitMetaItem) => {
    if (!item?.value) {
      return "—";
    }

    const valueContent = item.href ? (
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-slate-100 underline decoration-transparent transition hover:decoration-sky-300"
      >
        {item.value}
      </a>
    ) : (
      item.value
    );

    const extraLines = [item.detail, item.note].filter(
      (line): line is string => typeof line === "string" && line.trim().length > 0,
    );

    return (
        <div className="space-y-1 text-[13px]">
          <div className="font-medium text-slate-100">{valueContent}</div>
          {extraLines.map((line, index) => (
            <div key={index} className="text-[11px] text-slate-300 whitespace-pre-line">
              {line}
            </div>
          ))}
        </div>
    );
  };

  const resolveWithdrawalFeeBtc = (row: ExchangeComparisonRow) => {
    if (typeof row.withdrawalFeeBtc === "number" && Number.isFinite(row.withdrawalFeeBtc) && row.withdrawalFeeBtc > 0) {
      return row.withdrawalFeeBtc;
    }
    const fallback = EXCHANGE_META[row.id]?.withdrawalFeeBtc;
    return typeof fallback === "number" && Number.isFinite(fallback) && fallback > 0 ? fallback : null;
  };



  return (
    <div className="overflow-hidden rounded-2xl border border-slate-800/70 bg-slate-900/60 shadow-lg shadow-slate-950/30">
        <div className="flex flex-col gap-2 border-b border-slate-800/60 px-5 py-4 text-slate-300 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="flex items-center gap-2 text-lg font-semibold text-slate-100">
          <CompareIcon className="h-5 w-5 text-emerald-300" aria-hidden />
          <span>جدول مقایسه صرافی‌ها</span>
        </h3>
        <div className="text-xs sm:text-right text-slate-200">
          <span className="block text-[11px] text-slate-300">آخرین به‌روزرسانی اطلاعات ثابت: {LAST_UPDATED_SHAMSI}</span>
        </div>
      </div>
      <div className="w-full overflow-x-auto">
        <table className="w-full min-w-[720px] table-auto divide-y divide-slate-800/70 text-sm">
          <thead className="bg-slate-900/50 text-xs uppercase tracking-wide text-slate-200">
            <tr>
              <th scope="col" className="px-3 py-3 text-center align-middle" rowSpan={2}>صرافی</th>
              <th scope="col" className="px-3 py-3 text-center align-middle" rowSpan={2}>لایتنینگ</th>
              <th scope="col" className="px-3 py-3 text-right align-middle" rowSpan={2}>کارمزد برداشت</th>
              <th
                scope="colgroup"
                className="relative px-3 pt-3 pb-1 text-center align-middle"
                colSpan={2}
              >
                سقف برداشت روزانه
                <span className="pointer-events-none absolute inset-x-4 bottom-0 block h-px bg-slate-600" />
              </th>
              <th scope="col" className="px-3 py-3 text-right align-middle" rowSpan={2}>کارمزد معاملات</th>
            </tr>
            <tr>
              <th scope="col" className="px-3 py-2 text-right">بیت‌کوین</th>
              <th scope="col" className="px-3 py-2 text-right">تومان</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/60 text-slate-200">
            {rows.map((row) => {
              const isBest = bestRow?.id === row.id;
              const withdrawalFeeBtc = resolveWithdrawalFeeBtc(row);
              const referenceMid = typeof btcPriceToman === "number" && Number.isFinite(btcPriceToman)
                ? btcPriceToman
                : typeof row.mid === "number" && Number.isFinite(row.mid)
                  ? row.mid
                  : null;
              const withdrawalToman = typeof withdrawalFeeBtc === "number"
                && referenceMid !== null
                ? withdrawalFeeBtc * referenceMid
                : null;
              const lightningMessage = (() => {
                if (row.supportsLightning) {
                  if (row.lightningStatus === "active") {
                    return "شبکه لایتنینگ فعال است";
                  }
                  return row.lightningNote ?? "شبکه لایتنینگ موقتاً غیرفعال است";
                }
                return row.lightningNote ?? "شبکه لایتنینگ پشتیبانی نمی‌شود";
              })();
              const lightningBadgeClassName = row.supportsLightning
                ? row.lightningStatus === "active"
                  ? "inline-flex h-6 w-6 items-center justify-center rounded-full border border-amber-300/60 bg-amber-400/15 text-amber-300"
                  : "inline-flex h-6 w-6 items-center justify-center rounded-full border border-slate-300/70 bg-slate-100/15 text-slate-300"
                : "inline-flex h-6 w-6 items-center justify-center rounded-full border border-slate-600/70 bg-slate-900/50 text-slate-300";
              const showLightningSlash = !row.supportsLightning;
              const btcLimit = row.withdrawalLimits?.btc;
              const tomanLimit = row.withdrawalLimits?.toman;
              const feeSummaryLabel = row.feeSummary?.maxTakerFee != null
                ? `${percentFormatter.format(row.feeSummary.maxTakerFee)} و کمتر`
                : null;

              return (
                <tr key={row.id} className={isBest ? "bg-slate-800/50" : undefined}>
                  <th scope="row" className="px-3 py-4 align-top">
                    {row.link ? (
                      <a
                        href={row.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center gap-3 text-right font-semibold text-slate-100 transition hover:text-sky-200"
                        aria-label={`${row.name} - مشاهده در وب‌سایت صرافی`}
                      >
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-800/70 bg-slate-900/70 transition group-hover:border-sky-500/60">
                          {row.logo ? (
                            <picture>
                              {row.logoWebp ? <source srcSet={row.logoWebp} type="image/webp" /> : null}
                              <img
                                src={row.logo}
                                alt=""
                                loading="lazy"
                                width={36}
                                height={36}
                                className="h-7 w-7 rounded-lg object-contain"
                              />
                            </picture>
                          ) : (
                            <span className="text-base">{row.name.charAt(0)}</span>
                          )}
                        </span>
                        <span>{row.name}</span>
                      </a>
                    ) : (
                      <div className="flex items-center gap-3 text-right font-semibold text-slate-100">
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-800/70 bg-slate-900/70">
                          {row.logo ? (
                            <picture>
                              {row.logoWebp ? <source srcSet={row.logoWebp} type="image/webp" /> : null}
                              <img
                                src={row.logo}
                                alt={row.name}
                                loading="lazy"
                                width={36}
                                height={36}
                                className="h-7 w-7 rounded-lg object-contain"
                              />
                            </picture>
                          ) : (
                            <span className="text-base">{row.name.charAt(0)}</span>
                          )}
                        </span>
                        <span>{row.name}</span>
                      </div>
                    )}
                    {isBest ? (
                      <div className="mt-2 flex justify-end">
                        <span className="inline-flex rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-300">
                          بهترین {mode === "buy" ? "خرید" : "فروش"}
                        </span>
                      </div>
                    ) : null}
                  </th>
                  <td className="px-3 py-4 text-center align-top">
                    <span className="group relative inline-flex items-center justify-center">
                      <span
                        className={lightningBadgeClassName}
                        title={lightningMessage}
                        aria-label={lightningMessage}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          aria-hidden
                          className="h-4 w-4"
                          style={{ opacity: row.supportsLightning ? (row.lightningStatus === "active" ? 1 : 0.75) : 0.7 }}
                        >
                          <path d="M13 2 6 13h5l-1 9 7-11h-5z" fill="currentColor" />
                          {showLightningSlash ? (
                            <line
                              x1="6"
                              y1="6"
                              x2="18"
                              y2="18"
                              stroke="currentColor"
                              strokeWidth={1.8}
                              strokeLinecap="round"
                            />
                          ) : null}
                        </svg>
                      </span>
                      <span className="pointer-events-none absolute left-1/2 top-full mt-2 -translate-x-1/2 transform whitespace-nowrap rounded-md bg-slate-800 px-2 py-1 text-[11px] text-slate-100 opacity-0 shadow-lg shadow-slate-950/40 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                        {lightningMessage}
                      </span>
                    </span>
                  </td>
                  <td className="px-3 py-4 text-right align-top">
                    {typeof withdrawalFeeBtc === "number" && withdrawalFeeBtc > 0 ? (
                      (() => {
                        const sats = Math.round(withdrawalFeeBtc * 100_000_000);
                        return (
                          <div className="text-slate-100">
                            {`${satoshiFormatter.format(sats)} ساتوشی`}
                          <div className="text-xs text-slate-300">
                              {withdrawalToman != null && Number.isFinite(withdrawalToman)
                                ? `معادل ${tomanFormatter.format(Math.round(withdrawalToman))} تومان`
                                : ""}
                            </div>
                          </div>
                        );
                      })()
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-3 py-4 text-right align-top">{renderWithdrawalLimitCell(btcLimit)}</td>
                  <td className="px-3 py-4 text-right align-top">{renderWithdrawalLimitCell(tomanLimit)}</td>
                  <td className="px-3 py-4 text-right align-top whitespace-nowrap">
                    {row.feeSummary && feeSummaryLabel ? (
                      <a
                        href={row.feeSummary.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-100 underline decoration-transparent transition hover:decoration-sky-300"
                      >
                        {feeSummaryLabel}
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
